<template>
  <div class="cashier-order-audit">
    <div class="page-header">
      <h2>收银订单审计</h2>
      <p>集中查看退款、撤销、免费单和异常收银记录</p>
    </div>

    <div class="page-content">
      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <option v-for="store in storeList" :key="store.id" :value="store.id">{{ store.storeName }}</option>
          </select>
          <select v-model="auditType" class="store-select">
            <option value="">全部类型</option>
            <option value="REFUND">退款</option>
            <option value="REVOKE">撤销</option>
            <option value="FREE">免费单</option>
            <option value="MEMBER_BALANCE">会员余额扣费</option>
          </select>
          <input v-model="startTime" type="date" class="date-input" />
          <input v-model="endTime" type="date" class="date-input" />
          <button class="search-btn" @click="loadOrders">搜索</button>
          <button class="reset-btn" @click="handleReset">重置</button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <span>记录数</span>
          <strong>{{ filteredOrders.length }}</strong>
        </div>
        <div class="summary-card warning">
          <span>退款金额</span>
          <strong>¥{{ formatMoney(refundAmount) }}</strong>
        </div>
        <div class="summary-card danger">
          <span>撤销/免费单</span>
          <strong>{{ revokeCount }} / {{ freeCount }}</strong>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>审计列表</h3>
        </div>
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>订单号</th>
                <th>门店</th>
                <th>会员</th>
                <th>手机号</th>
                <th>金额</th>
                <th>支付方式</th>
                <th>审计类型</th>
                <th>操作人</th>
                <th>时间</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="10" class="empty-cell">加载中...</td>
              </tr>
              <tr v-else-if="filteredOrders.length === 0">
                <td colspan="10" class="empty-cell">暂无审计记录</td>
              </tr>
              <tr v-else v-for="order in filteredOrders" :key="order.id || order.orderNo">
                <td class="mono-cell">{{ order.orderNo || order.orderId || '-' }}</td>
                <td>{{ order.storeName || getStoreName(order.storeId) }}</td>
                <td>{{ order.customerName || order.memberName || '-' }}</td>
                <td>{{ order.phone || order.customerPhone || '-' }}</td>
                <td :class="['amount-cell', Number(order.amount || 0) < 0 ? 'negative' : '']">
                  ¥{{ formatMoney(order.amount || order.payAmount || 0) }}
                </td>
                <td>{{ order.paymentMethod || order.payTypeName || '-' }}</td>
                <td><span class="audit-tag">{{ getAuditTypeLabel(order) }}</span></td>
                <td>{{ order.operatorName || order.createdBy || '-' }}</td>
                <td>{{ formatDate(order.consumptionTime || order.createdTime || order.payTime) }}</td>
                <td>{{ order.remark || order.refundReason || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'CashierOrderAudit',
  data() {
    return {
      orders: [],
      storeList: [],
      searchStoreId: '',
      auditType: '',
      startTime: '',
      endTime: '',
      loading: false
    }
  },
  computed: {
    filteredOrders() {
      if (!this.auditType) return this.orders
      return this.orders.filter(order => this.getAuditType(order) === this.auditType)
    },
    refundAmount() {
      return this.orders
        .filter(order => this.getAuditType(order) === 'REFUND')
        .reduce((sum, order) => sum + Math.abs(Number(order.amount || order.payAmount || 0)), 0)
    },
    revokeCount() {
      return this.orders.filter(order => this.getAuditType(order) === 'REVOKE').length
    },
    freeCount() {
      return this.orders.filter(order => this.getAuditType(order) === 'FREE').length
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadOrders()
  },
  methods: {
    async loadStoreList() {
      const response = await request.get('/api/stores/getStores', { params: { page: 0, size: 1000 } })
      if (response.data.success && response.data.code === 200) {
        this.storeList = response.data.data.content || []
      }
    },
    async loadOrders() {
      this.loading = true
      try {
        const response = await request.get('/api/consumption/query', {
          params: {
            page: 0,
            size: 5000,
            storeId: this.searchStoreId || undefined,
            startTime: this.startTime || undefined,
            endTime: this.endTime || undefined
          }
        })
        if (response.data.success && response.data.code === 200) {
          this.orders = response.data.data.content || response.data.data || []
        }
      } finally {
        this.loading = false
      }
    },
    handleReset() {
      this.searchStoreId = ''
      this.auditType = ''
      this.startTime = ''
      this.endTime = ''
      this.loadOrders()
    },
    getStoreName(storeId) {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? store.storeName : '-'
    },
    getAuditType(order) {
      const status = String(order.status || order.orderStatus || '').toUpperCase()
      const payType = String(order.paymentMethod || order.payType || order.payTypeName || '').toUpperCase()
      if (status.includes('REFUND') || order.refundAmount) return 'REFUND'
      if (status.includes('REVOKE') || status.includes('CANCEL')) return 'REVOKE'
      if (payType.includes('FREE') || Number(order.amount || order.payAmount || 0) === 0) return 'FREE'
      if (payType.includes('MEMBER') || payType.includes('BALANCE') || payType.includes('余额')) return 'MEMBER_BALANCE'
      return 'NORMAL'
    },
    getAuditTypeLabel(order) {
      const labels = {
        REFUND: '退款',
        REVOKE: '撤销',
        FREE: '免费单',
        MEMBER_BALANCE: '会员余额',
        NORMAL: '普通消费'
      }
      return labels[this.getAuditType(order)] || '普通消费'
    },
    formatMoney(value) {
      return Number(value || 0).toFixed(2)
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('zh-CN')
    }
  }
}
</script>

<style scoped>
.page-header { margin-bottom: 24px; }
.page-header h2 { margin: 0 0 8px; color: #333; }
.page-header p { margin: 0; color: #666; }
.page-content, .table-section { background: #fff; border-radius: 8px; padding: 20px; }
.search-section { margin-bottom: 20px; }
.search-box { display: flex; flex-wrap: wrap; gap: 12px; }
.store-select, .date-input { height: 36px; padding: 0 12px; border: 1px solid #d9d9d9; border-radius: 6px; }
button { border: none; border-radius: 6px; padding: 8px 16px; cursor: pointer; }
.search-btn { background: #1890ff; color: #fff; }
.reset-btn { background: #f5f5f5; color: #333; }
.summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
.summary-card { padding: 16px; background: #f7f8fa; border-radius: 8px; }
.summary-card span, .summary-card strong { display: block; }
.summary-card span { color: #666; margin-bottom: 8px; }
.summary-card strong { font-size: 24px; color: #333; }
.summary-card.warning strong { color: #fa8c16; }
.summary-card.danger strong { color: #ff4d4f; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; min-width: 1100px; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px; border-bottom: 1px solid #f0f0f0; text-align: left; }
.empty-cell { text-align: center; color: #999; padding: 40px; }
.mono-cell { font-family: Consolas, monospace; color: #666; }
.amount-cell { color: #52c41a; font-weight: 600; }
.amount-cell.negative { color: #ff4d4f; }
.audit-tag { padding: 4px 8px; background: #e6f7ff; color: #1890ff; border-radius: 4px; font-size: 12px; }
@media (max-width: 900px) {
  .summary-grid { grid-template-columns: 1fr; }
}
</style>
